import { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Container,
  Row,
  Col,
  Table,
  Input,
  Label,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import axios from "axios";
import Header from "components/Headers/Header";
import Loader from "components/Loader";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL;

const AdManagerReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortColumn, setSortColumn] = useState("revenue");
  const [sortDirection, setSortDirection] = useState("desc");
  const itemsPerPage = 15;

  // 🔹 Buscar relatório do backend
  const fetchAdManagerData = async (custom = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const body =
        custom && startDate && endDate
          ? { startDate, endDate }
          : { relative: "TODAY" };

      const res = await axios.post(`${API_URL}/admanager/report`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setData(res.data.rows || []);
      setCurrentPage(1);
    } catch (err) {
      console.error("Erro ao buscar relatório do Ad Manager:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdManagerData(false);
  }, []);

  // 🔄 Ordenação
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const sortedData = [...data].sort((a, b) => {
    const valA = Number(a[sortColumn]) || 0;
    const valB = Number(b[sortColumn]) || 0;
    return sortDirection === "asc" ? valA - valB : valB - valA;
  });

  // 🔢 Paginação
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 📊 Agrupamento por domínio
  const aggregatedByDomain = Object.values(
    data.reduce((acc, item) => {
      const domain = item.adUnit || "(não identificado)";
      if (!acc[domain]) {
        acc[domain] = {
          adUnit: domain,
          unfilledImpressions: 0,
          impressions: 0,
          clicks: 0,
          revenue: 0,
          eCPM: 0,
          count: 0,
        };
      }
      acc[domain].unfilledImpressions += Number(item.unfilledImpressions || 0);
      acc[domain].impressions += Number(item.impressions || 0);
      acc[domain].clicks += Number(item.clicks || 0);
      acc[domain].revenue += Number(item.revenue || 0);
      acc[domain].eCPM += Number(item.eCPM || 0);
      acc[domain].count++;
      return acc;
    }, {})
  ).map((v) => ({
    adUnit: v.adUnit,
    unfilledImpressions: v.unfilledImpressions,
    impressions: v.impressions,
    clicks: v.clicks,
    revenue: v.revenue,
    avgECPM: v.count > 0 ? v.eCPM / v.count : 0,
  }));

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        {/* 🔍 Filtro de datas */}
        <Row className="mb-4">
          <Col md="3">
            <Label className="font-weight-bold text-dark">Início:</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>
          <Col md="3">
            <Label className="font-weight-bold text-dark">Fim:</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>
          <Col md="3" className="d-flex align-items-end justify-content-start">
            <Button color="primary" onClick={() => fetchAdManagerData(true)}>
              🔍 Consultar
            </Button>
          </Col>
        </Row>

        {/* 📊 Gráfico de Receita por Domínio */}
        <Row>
          <Col xl="12">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">💰 Receita por Domínio (Ad Manager)</h3>
              </CardHeader>
              <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
                {loading ? (
                  <Loader />
                ) : data.length === 0 ? (
                  <p className="text-center text-muted">Sem dados disponíveis.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={[...aggregatedByDomain]
                        .sort((a, b) => b.revenue - a.revenue)
                        .map((item) => ({
                          ...item,
                          short:
                            item.adUnit
                              ?.split(".")[0]
                              ?.replace(/https?:\/\//, "")
                              ?.replace(/www\./, "") || "(?)",
                        }))}
                    >
                      <XAxis dataKey="short" />
                      <YAxis />
                      <Tooltip formatter={(value) => `$${Number(value).toFixed(2)}`} />
                      <Bar dataKey="revenue" fill="#00C49F" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* 📋 Tabela detalhada */}
        <Row className="mt-4">
          <Col xl="12">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">📋 Detalhamento Diário — Ad Manager</h3>
              </CardHeader>

              {loading ? (
                <Loader />
              ) : (
                <>
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        {[
                          { key: "date", label: "📅 Data" },
                          { key: "adUnit", label: "🌐 Domínio" },
                          { key: "unfilledImpressions", label: "⚠️ Unfilled" },
                          { key: "impressions", label: "👁️ Impressões" },
                          { key: "clicks", label: "🖱️ Cliques" },
                          { key: "revenue", label: "💰 Receita (USD)" },
                          { key: "eCPM", label: "📈 eCPM (USD)" },
                        ].map((col) => (
                          <th
                            key={col.key}
                            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                            onClick={() => handleSort(col.key)}
                          >
                            {col.label}{" "}
                            {sortColumn === col.key
                              ? sortDirection === "asc"
                                ? "▲"
                                : "▼"
                              : ""}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((row, idx) => (
                        <tr key={idx}>
                          <td>
                            {row.date
                              ? (() => {
                                const str = row.date.toString();
                                if (/^\d{8}$/.test(str)) {
                                  const y = str.substring(0, 4);
                                  const m = str.substring(4, 6);
                                  const d = str.substring(6, 8);
                                  return `${d}/${m}/${y}`;
                                }
                                return row.date;
                              })()
                              : "-"}
                          </td>
                          <td>{row.adUnit}</td>
                          <td>{row.unfilledImpressions}</td>
                          <td>{row.impressions}</td>
                          <td>{row.clicks}</td>
                          <td>${Number(row.revenue).toFixed(2)}</td>
                          <td>${Number(row.eCPM).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* 📄 Paginação */}
                  <CardFooter className="py-4">
                    <Pagination className="pagination justify-content-end mb-0">
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1)
                              setCurrentPage(currentPage - 1);
                          }}
                        >
                          <i className="fas fa-angle-left" />
                        </PaginationLink>
                      </PaginationItem>

                      {Array.from({ length: totalPages }, (_, i) => (
                        <PaginationItem
                          key={i + 1}
                          active={currentPage === i + 1}
                        >
                          <PaginationLink
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(i + 1);
                            }}
                          >
                            {i + 1}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < totalPages)
                              setCurrentPage(currentPage + 1);
                          }}
                        >
                          <i className="fas fa-angle-right" />
                        </PaginationLink>
                      </PaginationItem>
                    </Pagination>
                  </CardFooter>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AdManagerReport;